<script setup lang="ts">
import { Tooltip } from '@ark-ui/vue';
import { ref, computed, watch, nextTick, onUnmounted, type Ref } from 'vue';
import UiText from './UiText.vue';

interface UiTooltipProps {
  content: string;
  openDelay?: number;
  closeDelay?: number;
  /** Tooltip follows the mouse cursor */
  followCursor?: boolean;
  /** Visual variant: 'default' has arrow, 'rounded' is a pill without arrow */
  variant?: 'default' | 'rounded';
  /** Disables the tooltip (both modes) while keeping the trigger wrapper mounted, so it is safe to toggle reactively. */
  disabled?: boolean;
}

const props = withDefaults(defineProps<UiTooltipProps>(), {
  openDelay: 300,
  closeDelay: 0,
  followCursor: false,
  variant: 'default',
  disabled: false,
});

// Use the actual pointer type so touch also works on hybrid devices.
const open = ref(false);
const touchInteraction = ref(false);
let triggerElement: HTMLElement | null = null;

const handlePointerDown = (event: PointerEvent) => {
  touchInteraction.value = event.pointerType === 'touch';
  triggerElement = event.currentTarget as HTMLElement;
};

const handleClick = async (event: MouseEvent) => {
  if (!touchInteraction.value || props.disabled || event.detail === 0) return;
  if (props.followCursor) {
    if (shouldExcludeTooltip(event.target as HTMLElement)) return;
    updatePosition(event);
  }
  // Let Ark finish processing the touch pointerleave before opening.
  await nextTick();
  // Click fires after a completed tap, so swiping does not open a tooltip.
  if (!props.disabled) open.value = !open.value;
};

watch(open, (isOpen, _, onCleanup) => {
  if (!isOpen) return;
  const doc = triggerElement?.ownerDocument ?? document;
  const dismiss = () => { open.value = false; };
  const onPointerDown = (event: PointerEvent) => {
    if (!triggerElement?.contains(event.target as Node)) dismiss();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' || event.key === 'Tab') dismiss();
  };
  // Capture catches taps even when a child stops propagation.
  doc.addEventListener('pointerdown', onPointerDown, true);
  doc.addEventListener('keydown', onKeyDown, true);
  doc.addEventListener('scroll', dismiss, true);
  onCleanup(() => {
    doc.removeEventListener('pointerdown', onPointerDown, true);
    doc.removeEventListener('keydown', onKeyDown, true);
    doc.removeEventListener('scroll', dismiss, true);
  });
});

watch(() => props.disabled, (disabled) => {
  if (disabled) {
    open.value = false;
    handleMouseLeave();
  }
});

// Mouse tracking for follow cursor mode
const containerRef = ref<HTMLElement | null>(null) as Ref<HTMLElement | null>;
const pillRef = ref<HTMLElement | null>(null) as Ref<HTMLElement | null>;

// Current position (updated immediately for smooth tracking)
const currentX = ref(0);
const currentY = ref(0);

// Visibility states
const isMouseInside = ref(false);
const isVisible = ref(false);
const isReady = ref(false); // Prevents showing before first position is set
const isOverNestedTooltip = ref(false); // Hide when over element with its own tooltip

// Delay timer
let showTimer: ReturnType<typeof setTimeout> | null = null;

const PILL_OFFSET = 12;

/**
 * Check if the target element or any of its ancestors should exclude the follow-cursor tooltip.
 * This includes:
 * - Ark UI tooltip triggers (data-scope="tooltip")
 * - Elements marked with data-has-tooltip
 * - Elements marked with data-tooltip-exclude (for excluding areas without their own tooltip)
 */
const shouldExcludeTooltip = (target: HTMLElement): boolean => {
  let el: HTMLElement | null = target;
  
  while (el && el !== containerRef.value) {
    // Check for Ark UI tooltip trigger
    if (el.hasAttribute('data-scope') && el.getAttribute('data-scope') === 'tooltip') {
      return true;
    }
    // Check for custom marker attribute (element has its own tooltip)
    if (el.hasAttribute('data-has-tooltip')) {
      return true;
    }
    // Check for exclusion marker (element should hide follow-cursor tooltip)
    if (el.hasAttribute('data-tooltip-exclude')) {
      return true;
    }
    // Check if parent is a tooltip trigger (Ark UI wraps triggers)
    if (el.closest('[data-scope="tooltip"][data-part="trigger"]')) {
      return true;
    }
    el = el.parentElement;
  }
  
  return false;
};

const updatePosition = (e: MouseEvent) => {
  if (!containerRef.value) return;
  
  const rect = containerRef.value.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  
  // Get pill dimensions for positioning calculation
  const pillWidth = pillRef.value?.offsetWidth || 120;
  const pillHeight = pillRef.value?.offsetHeight || 32;
  
  const spaceOnRight = rect.width - x;
  const shouldFlipLeft = spaceOnRight < pillWidth + PILL_OFFSET * 2;
  
  // Calculate X position with bounds clamping
  let newX: number;
  if (shouldFlipLeft) {
    // Position to the left of cursor, but don't go past left edge
    newX = Math.max(0, x - pillWidth - PILL_OFFSET);
  } else {
    // Position to the right of cursor, but don't go past right edge
    newX = Math.min(rect.width - pillWidth, x + PILL_OFFSET);
  }
  
  // Calculate Y position, clamped to container bounds
  const newY = Math.min(rect.height - pillHeight, Math.max(0, y + PILL_OFFSET));
  
  currentX.value = newX;
  currentY.value = newY;
  
  // Mark as ready after first position update
  if (!isReady.value) {
    isReady.value = true;
  }
};

const handleMouseMove = (e: PointerEvent) => {
  if (!props.followCursor || props.disabled || e.pointerType === 'touch') return;
  
  // Check if we're over an element that should exclude the tooltip
  const target = e.target as HTMLElement;
  isOverNestedTooltip.value = shouldExcludeTooltip(target);
  
  updatePosition(e);
};

const handleMouseEnter = (e: PointerEvent) => {
  if (!props.followCursor || props.disabled || e.pointerType === 'touch') return;
  
  isMouseInside.value = true;
  
  // Check if entering over an element that should exclude the tooltip
  const target = e.target as HTMLElement;
  isOverNestedTooltip.value = shouldExcludeTooltip(target);
  
  // Set initial position immediately (before showing)
  updatePosition(e);
  
  // Clear any existing timer
  if (showTimer) {
    clearTimeout(showTimer);
  }
  
  // Show after delay
  showTimer = setTimeout(() => {
    if (isMouseInside.value) {
      isVisible.value = true;
    }
  }, props.openDelay);
};

const handleMouseLeave = () => {
  isMouseInside.value = false;
  isVisible.value = false;
  isReady.value = false;
  isOverNestedTooltip.value = false;
  
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
};

// Cleanup timer on unmount
onUnmounted(() => {
  if (showTimer) {
    clearTimeout(showTimer);
  }
});

// Computed visibility - hide when over nested tooltip
const shouldShow = computed(() =>
  !props.disabled && (open.value || isVisible.value) && isReady.value && !isOverNestedTooltip.value
);

// Use transform for GPU-accelerated smooth movement
const pillStyle = computed(() => ({
  transform: `translate3d(${currentX.value}px, ${currentY.value}px, 0)`,
  opacity: shouldShow.value ? 1 : 0,
}));

const contentClasses = computed(() => [
  props.variant === 'rounded' ? 'se-tooltip-rounded' : 'se-tooltip-content',
]);
</script>

<template>
  <!-- Follow cursor mode: custom implementation -->
  <div 
    v-if="props.followCursor"
    ref="containerRef"
    class="relative"
    @pointermove="handleMouseMove"
    @pointerenter="handleMouseEnter"
    @pointerleave="handleMouseLeave"
    @pointerdown="handlePointerDown"
    @click="handleClick"
  >
    <slot />
    <div 
      ref="pillRef"
      class="pointer-events-none absolute left-0 top-0 z-50 px-3 py-1.5 bg-se-dark text-se-white text-sm font-medium rounded-full whitespace-nowrap transition-opacity duration-150 ease-out will-change-transform"
      :style="pillStyle"
    >
      {{ props.content }}
    </div>
  </div>

  <!-- Standard tooltip mode -->
  <Tooltip.Root
    v-else
    v-model:open="open"
    :open-delay="props.openDelay"
    :close-delay="props.closeDelay"
    :disabled="props.disabled"
    :close-on-click="!touchInteraction"
    :close-on-pointer-down="!touchInteraction"
  >
    <Tooltip.Trigger as-child data-has-tooltip @pointerdown="handlePointerDown" @click="handleClick">
      <slot />
    </Tooltip.Trigger>
    <Tooltip.Positioner>
      <Tooltip.Content :class="contentClasses">
        <template v-if="props.variant === 'default'">
          <Tooltip.Arrow>
            <Tooltip.ArrowTip />
          </Tooltip.Arrow>
        </template>
        <UiText as="p" variant="tooltip" :text="props.content" />
      </Tooltip.Content>
    </Tooltip.Positioner>
  </Tooltip.Root>
</template>

